import type { NLT } from '@nicelittlethings/types'
import { retryWithBackoff } from '../utils/retry'

// Use official types from @nicelittlethings/types
export type CreateDesignResponse = NLT.DesignStudio.CreateDesignResponse
export type CreateDesignRequest = NLT.DesignStudio.CreateDesignRequest
export type GetDesignResponse = NLT.DesignStudio.GetDesignResponse
export type UpdateDesignRequest = NLT.DesignStudio.UpdateDesignRequest

// eslint-disable-next-line no-restricted-syntax
export class UploadUrlApiError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(status: number, code: string | null, message: string) {
    super(message)
    this.name = 'UploadUrlApiError'
    this.status = status
    this.code = code
  }
}

/**
 * Design API Client
 */
// Type for the design API client
export type DesignApi = ReturnType<typeof createDesignApiClient>

export const createDesignApiClient = () => {
  const createDesign = async (request: CreateDesignRequest): Promise<CreateDesignResponse> => {
    try {
      const response = await fetch(`/api/designs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add any additional headers that might be needed for CORS
          Accept: 'application/json',
        },
        body: JSON.stringify(request),
        // Ensure cookies are sent if needed
        credentials: 'omit', // Change to 'include' if API requires cookies
      })

      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} ${response.statusText}`
        try {
          const errorData = await response.json()
          errorMessage = `API Error: ${response.status} - ${errorData.error || errorData.message || response.statusText}`
        } catch {
          // If response is not JSON, use default message
        }
        throw new Error(errorMessage)
      }

      const data = await response.json()
      return data as CreateDesignResponse
    } catch (error) {
      // Add more context to network errors
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error(
          'Network error: Unable to reach the API. This might be a CORS issue if running locally.',
        )
      }
      throw error
    }
  }

  const loadDesign = async (designId: string): Promise<CreateDesignResponse> => {
    try {
      const response = await fetch(`/api/designs/${designId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'omit',
      })

      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} ${response.statusText}`
        try {
          const errorData = await response.json()
          errorMessage = `API Error: ${response.status} - ${errorData.error || errorData.message || response.statusText}`
        } catch {
          // If response is not JSON, use default message
        }
        throw new Error(errorMessage)
      }

      const data = await response.json()
      return data as CreateDesignResponse
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error(
          'Network error: Unable to reach the API. This might be a CORS issue if running locally.',
        )
      }
      throw error
    }
  }

  const updateDesign = async (
    designId: string,
    request: UpdateDesignRequest,
    signal?: AbortSignal,
    onRetry?: (attempt: number, error: Error) => void,
  ): Promise<void> => {
    const doSave = async () => {
      const response = await fetch(`/api/designs/${designId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(request),
        credentials: 'omit',
        signal, // Pass abort signal to fetch
      })

      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} ${response.statusText}`
        try {
          const errorData = await response.json()
          errorMessage = `API Error: ${response.status} - ${errorData.error || errorData.message || response.statusText}`
        } catch {
          // If response is not JSON, use default message
        }
        throw new Error(errorMessage)
      }
    }

    try {
      // Use retry with exponential backoff
      await retryWithBackoff(doSave, {
        maxRetries: 3,
        initialDelay: 1000,
        maxDelay: 8000,
        backoffMultiplier: 2,
        signal,
        onRetry,
      })
    } catch (error) {
      // Don't treat abort as an error
      if (error instanceof Error && error.name === 'AbortError') {
        return
      }
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error(
          'Network error: Unable to reach the API. This might be a CORS issue if running locally.',
        )
      }
      throw error
    }
  }

  const getUploadUrl = async (
    designId: string,
    request: { filename: string; contentType: string },
  ): Promise<{ uploadUrl: string; fileUrl: string }> => {
    const url = `/api/designs/${designId}/upload`
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(request),
        credentials: 'omit',
      })

      if (!response.ok) {
        let code: string | null = null
        let message = `API Error: ${response.status} ${response.statusText}`
        try {
          const errorData = await response.json()
          code = typeof errorData?.code === 'string' ? errorData.code : null
          message =
            errorData?.message ||
            errorData?.error ||
            `API Error: ${response.status} ${response.statusText}`
        } catch {
          // If response is not JSON, use default message
        }
        throw new UploadUrlApiError(response.status, code, message)
      }

      const data = await response.json()
      return data as { uploadUrl: string; fileUrl: string }
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error(
          'Network error: Unable to reach the upload API. Please check your connection.',
        )
      }
      throw error
    }
  }

  const uploadToS3 = async (
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<void> => {
    try {
      const xhr = new XMLHttpRequest()
      return new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) {
            const progress = (e.loaded / e.total) * 100
            onProgress(progress)
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`))
          }
        })

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed due to network error'))
        })

        xhr.open('PUT', uploadUrl)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.send(file)
      })
    } catch (error) {
      console.error('S3 upload error:', error)
      throw new Error('Failed to upload image. Please try again.')
    }
  }

  return {
    createDesign,
    loadDesign,
    updateDesign,
    saveDesign: updateDesign, // Alias for backward compatibility
    getUploadUrl,
    uploadToS3,
  }
}

// Default API client instance
export const designApi = createDesignApiClient()
